import { useState, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { X } from 'lucide-react'

import { TransactionListItem } from '@/components/transaction/transaction-list-item'
import { Skeleton } from '@/components/ui/skeleton'
import { MonthSelector } from '@/components/month-selector'
import { FilterChip } from '@/components/filter-chip'
import { SCOPE_LABEL, SCOPE_OPTIONS, EXPENSE_TYPE_LABEL, EXPENSE_TYPE_OPTIONS, PAID_BY_LABEL, buildPaidByOptions } from '@/components/filter-chip-options'
import { getCurrentMonth, filterByExpenseType, filterByPaidBy } from '@/lib/utils'
import { useTransactions, useScope, useExpenseType, useProfiles, usePaidBy } from '@/lib/hooks'


export default function TransactionsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [month, setMonth] = useState(() => searchParams.get('month') ?? getCurrentMonth())
  const categoryFilter = searchParams.get('category')
  const { scope, setScope } = useScope()
  const { expenseType, setExpenseType } = useExpenseType()
  const { paidBy, setPaidBy } = usePaidBy()
  const { data: profiles = [] } = useProfiles()
  const paidByOptions = useMemo(() => buildPaidByOptions(profiles), [profiles])

  const { data: transactions = [], isLoading: loading } = useTransactions(month)
  const byCategory = categoryFilter
    ? transactions.filter((tx) =>
        tx.items.some((it) => it.parent_category_name === categoryFilter),
      )
    : transactions
  const filtered = filterByPaidBy(filterByExpenseType(byCategory, expenseType), paidBy)

  const clearCategoryFilter = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('category')
    setSearchParams(next, { replace: true })
  }

  return (
    <div className="bg-background">
      <MonthSelector month={month} onMonthChange={setMonth} />

      {categoryFilter && (
        <div className="mx-4 mt-2 flex items-center justify-between gap-2 rounded-md bg-muted/50 px-3 py-1.5 text-sm">
          <span className="min-w-0 truncate">
            カテゴリ: <span className="font-medium">{categoryFilter}</span>
          </span>
          <button
            type="button"
            onClick={clearCategoryFilter}
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label="カテゴリフィルタを解除"
          >
            <X className="size-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 px-4 pt-3 pb-1">
        <FilterChip
          label={SCOPE_LABEL}
          value={scope}
          onChange={setScope}
          options={SCOPE_OPTIONS}
        />
        <FilterChip
          label={EXPENSE_TYPE_LABEL}
          value={expenseType}
          onChange={setExpenseType}
          options={EXPENSE_TYPE_OPTIONS}
        />
        <FilterChip
          label={PAID_BY_LABEL}
          value={paidBy}
          onChange={setPaidBy}
          options={paidByOptions}
        />
      </div>

      {/* Transaction List */}
      {loading ? (
        <div className="space-y-1 p-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between px-2 py-3">
              <div className="flex items-center gap-3">
                <Skeleton className="h-4 w-10" />
                <div className="space-y-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground">
          この月の取引はありません
        </p>
      ) : (
        <div className="space-y-1 p-4">
          {filtered.map((tx) => (
            <TransactionListItem
              key={tx.id}
              transaction={tx}
              to={`/transactions/${tx.id}?month=${month}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
